import {useState} from 'react'
import {Pressable, View} from 'react-native'
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  OnboardingControls,
  OnboardingDescriptionText,
  OnboardingPosition,
  OnboardingTitleText,
} from '#/screens/Onboarding/Layout'
import {
  type AccountType,
  useOnboardingInternalState,
} from '#/screens/Onboarding/state'
import {
  COUNTRY_CURRICULA,
  COUNTRY_LIST,
} from '#/screens/Onboarding/StepGoal/data'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as TextField from '#/components/forms/TextField'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'

/**
 * The four kinds an account can be, in the words the step offers them in.
 *
 * The kind a person picks here is the kind the badge under their handle and the
 * sub-line of their posts' bylines draw them with (`ORG_TYPE_ICONS`,
 * `CREATOR_ROLES`), so the four glyphs below are the ones a participant sees
 * again wherever the app names them.
 */
const ROLES: {
  id: AccountType
  icon: string
  label: MessageDescriptor
  desc: MessageDescriptor | null
}[] = [
  {
    id: 'learner',
    icon: '🎓',
    label: msg`Improve skills and boost grades (Learner)`,
    desc: null,
  },
  {
    id: 'parent',
    icon: '👨‍👩‍👧',
    label: msg`Track my child's progress (Parent)`,
    desc: null,
  },
  {
    id: 'creator',
    icon: '🎤',
    label: msg`Share knowledge (Content Creator)`,
    desc: null,
  },
  {
    id: 'organisation',
    icon: '🏢',
    label: msg`Represent an Organisation`,
    desc: msg`For schools, universities, publishers, libraries, EdTech companies, NGOs, and any institution.`,
  },
]

export function StepGoalSelection() {
  const {i18n, t: l} = useLingui()
  const t = useTheme()
  const {state, dispatch} = useOnboardingInternalState()

  const [accountType, setAccountType] = useState<AccountType | null>(
    state.goalStepResults.accountType,
  )
  const [country, setCountry] = useState<string | null>(
    state.goalStepResults.country,
  )
  const [displayName, setDisplayName] = useState<string>(
    state.goalStepResults.displayName,
  )

  const selectedCountry = country ? COUNTRY_CURRICULA[country] : null
  const canContinue = !!accountType && !!country

  function onContinue() {
    if (!accountType || !country) return
    dispatch({
      type: 'setGoalAccountType',
      accountType,
      country,
      displayName,
    })
    dispatch({type: 'next'})
  }

  return (
    <View style={[a.align_start, a.gap_sm]} testID="onboardingGoal">
      <OnboardingPosition />
      <OnboardingTitleText>
        <Trans>What brings you here? 🎯</Trans>
      </OnboardingTitleText>
      <OnboardingDescriptionText>
        <Trans>
          Tell us your goal and country — we'll build the right experience
          around both.
        </Trans>
      </OnboardingDescriptionText>

      <TextField.Root style={[a.w_full]}>
        <TextField.Input
          label={l`Display name`}
          placeholder={l`Your display name`}
          value={displayName}
          onChangeText={setDisplayName}
          autoCapitalize="words"
          autoCorrect={false}
        />
      </TextField.Root>

      <View style={[a.w_full, a.pt_sm, a.gap_md]}>
        <Text style={[a.text_sm, a.font_bold, t.atoms.text_contrast_medium]}>
          <Trans>What's your goal?</Trans>
        </Text>
        <Toggle.Group
          values={accountType ? [accountType] : []}
          onChange={vals =>
            setAccountType((vals[vals.length - 1] as AccountType) ?? null)
          }
          label={l`Select your role`}>
          <View>
            {ROLES.map((role, idx) => (
              <Toggle.Item
                key={role.id}
                name={role.id}
                label={i18n._(role.label)}>
                {({selected}) => (
                  <View
                    style={[
                      a.flex_row,
                      a.align_center,
                      a.gap_md,
                      a.py_md,
                      idx < ROLES.length - 1 && {
                        borderBottomWidth: 1,
                        borderBottomColor:
                          t.atoms.border_contrast_low.borderColor,
                      },
                    ]}>
                    <View
                      style={[
                        {
                          width: 20,
                          height: 20,
                          borderRadius: 10,
                          borderWidth: 2,
                          flexShrink: 0,
                          alignItems: 'center' as const,
                          justifyContent: 'center' as const,
                          borderColor: selected
                            ? t.palette.primary_500
                            : t.atoms.border_contrast_medium.borderColor,
                        },
                      ]}>
                      {selected && (
                        <View
                          style={[
                            {
                              width: 10,
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: t.palette.primary_500,
                            },
                          ]}
                        />
                      )}
                    </View>
                    <View style={[a.flex_1]}>
                      <Text style={[a.text_md, a.font_bold]} emoji>
                        {role.icon} {i18n._(role.label)}
                      </Text>
                      {role.desc ? (
                        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                          {i18n._(role.desc)}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                )}
              </Toggle.Item>
            ))}
          </View>
        </Toggle.Group>
      </View>

      <View style={[a.w_full, a.pt_md, a.gap_md]}>
        <Text style={[a.text_sm, a.font_bold, t.atoms.text_contrast_medium]}>
          🌍 <Trans>Your country</Trans>
        </Text>
        {selectedCountry ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={l`Clear country selection`}
            accessibilityHint={l`Tap to deselect and choose a different country`}
            onPress={() => setCountry(null)}>
            <View
              style={[
                a.flex_row,
                a.align_center,
                a.justify_between,
                {
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  borderRadius: 10,
                  borderWidth: 1,
                  borderColor: t.atoms.border_contrast_low.borderColor,
                  backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
                },
              ]}>
              <Text style={[a.text_sm, a.font_bold]} emoji>
                {selectedCountry.flag} {selectedCountry.name}
              </Text>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                × change
              </Text>
            </View>
          </Pressable>
        ) : (
          <Toggle.Group
            values={country ? [country] : []}
            onChange={vals => setCountry(vals[vals.length - 1] ?? null)}
            label={l`Select your country`}>
            <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
              {COUNTRY_LIST.map(c => (
                <Toggle.Item key={c.code} name={c.code} label={c.name}>
                  {({selected}) => (
                    <View
                      style={[
                        a.px_md,
                        a.py_sm,
                        a.rounded_full,
                        a.border,
                        selected
                          ? {
                              borderColor: 'transparent',
                              ...t.atoms.bg_contrast_975,
                            }
                          : t.atoms.border_contrast_low,
                      ]}>
                      <Text
                        style={[
                          a.text_sm,
                          a.font_bold,
                          selected && t.atoms.text_inverted,
                        ]}
                        emoji>
                        {c.flag} {c.name}
                      </Text>
                    </View>
                  )}
                </Toggle.Item>
              ))}
            </View>
          </Toggle.Group>
        )}
      </View>

      <OnboardingControls.Portal>
        <Button
          disabled={!canContinue}
          testID="onboardingContinue"
          color="primary"
          size="large"
          label={l`Continue to next step`}
          onPress={onContinue}>
          <ButtonText>
            <Trans>Continue</Trans>
          </ButtonText>
        </Button>
      </OnboardingControls.Portal>
    </View>
  )
}
